import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion } from 'framer-motion';
import { Upload, FileText, AlertCircle, Loader2 } from 'lucide-react';

const FileUpload = ({ onFileUpload, loading, disabled, disabledReason, onDisabledAction, disabledActionLabel }) => {
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      console.error('Rejected files:', rejectedFiles);
      return;
    }

    if (acceptedFiles.length > 0) {
      onFileUpload(acceptedFiles[0]);
    }
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/json': ['.json'],
      'application/x-parquet': ['.parquet'],
      'application/octet-stream': ['.parquet']
    },
    maxFiles: 1,
    disabled: loading || disabled,
  });

  return (
    <div className="max-w-4xl mx-auto">
      {disabled && disabledReason && (
        <div className="mb-6 rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-4 text-sm text-sand-700 shadow-brand-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>{disabledReason}</div>
            {onDisabledAction && (
              <button
                type="button"
                onClick={onDisabledAction}
                className="inline-flex items-center justify-center rounded-lg bg-bill-500 px-4 py-2 text-xs font-semibold text-white hover:bg-bill-600 shadow-brand-sm"
              >
                {disabledActionLabel || 'Create free account'}
              </button>
            )}
          </div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
        id="file-upload-dropzone"
      >
        <div
          {...getRootProps()}
          className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer ${isDragActive && !isDragReject
              ? 'border-bill-500 bg-bill-100'
              : isDragReject
                ? 'border-red-400 bg-red-50'
                : 'border-sand-400 bg-sand-100 hover:border-bill-500 hover:bg-bill-100'
            } ${(loading || disabled) ? 'pointer-events-none opacity-50' : ''}`}
        >
          <input {...getInputProps()} />

          <div className="space-y-6">
            {loading ? (
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="flex flex-col items-center"
              >
                <Loader2 className="w-16 h-16 text-bill-500 animate-spin mb-4" />
                <h3 className="text-xl font-semibold text-ink">Uploading...</h3>
                <p className="text-sand-700">Processing your CSV file</p>
              </motion.div>
            ) : isDragReject ? (
              <div className="flex flex-col items-center">
                <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-red-600">Invalid File Type</h3>
                <p className="text-red-500">Please upload a CSV file only</p>
              </div>
            ) : (
              <motion.div
                animate={{
                  scale: isDragActive ? 1.05 : 1,
                  rotate: isDragActive ? 2 : 0
                }}
                className="flex flex-col items-center"
              >
                <div className="relative mb-6">
                  <div className="p-4 bg-bill-100 rounded-2xl border border-bill-300">
                    <Upload className="w-12 h-12 text-bill-600" />
                  </div>
                  {isDragActive && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -inset-2 bg-bill-100 rounded-3xl -z-10"
                    />
                  )}
                </div>

                <h3 className="text-2xl font-bold text-ink mb-2">
                  {isDragActive ? 'Drop your file here' : 'Upload your data file'}
                </h3>

                <p className="text-sand-700 mb-6 max-w-md">
                  Drag and drop your data file here, or click to browse.
                  Supported formats: CSV, Excel (XLSX), JSON, and Parquet.
                  We'll analyze your data and make it queryable with AI.
                </p>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary"
                  type="button"
                >
                  Choose File
                </motion.button>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>


      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        {[
          {
            icon: FileText,
            title: 'Multiple Formats',
            description: 'Upload CSV, Excel, JSON, or Parquet files with headers and data'
          },
          {
            icon: Upload,
            title: 'Instant Processing',
            description: 'Files are processed and ready to query immediately'
          },
          {
            icon: AlertCircle,
            title: 'Smart Analysis',
            description: 'AI automatically understands your data structure'
          }
        ].map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + index * 0.1 }}
            className="card text-center bg-white border border-[rgba(32,30,29,0.12)]"
          >
            <div className="p-3 bg-sand-100 rounded-xl w-fit mx-auto mb-4 border border-[rgba(32,30,29,0.12)]">
              <feature.icon className="w-6 h-6 text-bill-600" />
            </div>
            <h4 className="font-semibold text-ink mb-2">{feature.title}</h4>
            <p className="text-sand-700 text-sm">{feature.description}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default FileUpload;
